import { v4 as uuid } from 'uuid'
import { format, addMonths } from 'date-fns'
import { coverageTypes, policyTypes, redshiftDateFormat } from '../constants'

export const demoUser = {
  firstName: 'Demo',
  lastName: 'User',
  email: 'demouser@stockguard.io',
  password: '123',
  confirmedAt: new Date(),
}

const { password: _password, ...demoUserWithoutPassword } = demoUser

export const demoUserProfile = {
  farmerType: 'Beginning Farmer/Rancher',
  maritalStatus: 'Not Married',
  ssn: '000-00-0000',
  dob: new Date('11/05/1985').toISOString(),
  liveStock: 'Feeder Cattle',
}

const demoUserLocation = {
  addressLine1: 'New York',
  addressLine2: '',
  city: 'New York',
  county: 'County',
  state: 'NY',
  zip: '10005',
}

const basePolicy = {
  ...demoUserWithoutPassword,
  ...demoUserLocation,
  ...demoUserProfile,
  status: 'Active',
  applicationType: 'individual',
  cond1: false,
  cond2: false,
  cond3: false,
  cond4: false,
  cond5: false,
  cond6: false,
  phone: '8009228401',
  livestock: {
    Swine: true,
  },
  maritalStatus: false,
  recipientName: 'Recipient',
  applicationOwnerType: 'Farmer',
  applicationTypeOption: 'Beginning Farmer/Rancher',
  previousPolicyProvider: '',
  shares: [
    {
      ...demoUserWithoutPassword,
      ...demoUserLocation,
      shares: 100,
      ownerType: 'Individual',
      applicationTypeOption: 'Beginning Farmer/Rancher',
      phone: '0000000000',
      address: demoUserLocation.addressLine1,
    },
  ],
  signatures: [
    {
      fullName: `${demoUser.firstName} ${demoUser.lastName}`,
      signedAt: new Date(),
      isAdditionalSigner: false,
    },
  ],
  signatureFullName: 'Demo User',
}

export const demoUserLrpPolicy = {
  ...basePolicy,
  id: uuid(),
  type: policyTypes.lrp,
  applicationFor: policyTypes.lrp,
}

export const demoUserLgmPolicy = {
  ...basePolicy,
  id: uuid(),
  type: policyTypes.lgm,
  applicationFor: policyTypes.lgm,
}

export const demoUserSwineCoverages = [
  {
    type: coverageTypes.lrp,
    commodity: 'Swine',
    commodityType: 'No Type Specified',
    status: 'Expired',
    salesEffectiveDate: '2022-11-16',
    details: {
      endDate: '2023-02-15',
      endorsementLength: '13',
      targetWeight: 1.4,
      numberofHead: 200,
      premium: 3.38,
      expectedEndingValue: 90.682,
      coverageLevel: 0.98211,
      coveragePrice: 90,
      expectedIndemnity: null,
      actualEndingValue: 75.62,
      actualIndemnity: 0,
    },
  },
  {
    type: coverageTypes.lrp,
    commodity: 'Swine',
    commodityType: 'No Type Specified',
    status: 'Active',
    salesEffectiveDate: '2023-10-27',
    details: {
      endDate: '2024-03-22',
      endorsementLength: '13',
      targetWeight: 1.7,
      numberofHead: 150,
      premium: 3.79535,
      expectedEndingValue: 78.833,
      coverageLevel: 0.98829,
      coveragePrice: 77.91,
      expectedIndemnity: null,
      actualEndingValue: null,
      actualIndemnity: null,
    },
  },
  {
    type: coverageTypes.lrp,
    commodity: 'Swine',
    commodityType: 'No Type Specified',
    status: 'Pending',
    salesEffectiveDate: '2023-11-08',
    details: {
      endDate: '2024-02-07',
      endorsementLength: '13',
      targetWeight: 2.6,
      numberofHead: 180,
      premium: 4.81065,
      expectedEndingValue: 74.85,
      coverageLevel: 0.98864,
      coveragePrice: 74,
      expectedIndemnity: null,
      actualEndingValue: null,
      actualIndemnity: null,
    },
  },
]

const baseLgmCoverageSed = '2023-11-09'
const baseLgmCoverageSedDate = new Date('2023-11-09')

const baseLgmCoverage = () => ({
  type: coverageTypes.lgm,
  status: 'Active',
  salesEffectiveDate: baseLgmCoverageSed,
  details: {
    deductible: 0,
    marketingMonths: [
      {
        premium: 37.9,
        subsidy: 8.33,
        numberofHead: 20,
        marketingMonth: format(
          addMonths(baseLgmCoverageSedDate, 2),
          redshiftDateFormat,
        ),
        actualIndemnity: null,
        expectedIndemnity: null,
        actualGrossMargin: null,
        expectedGrossMargin: 656.03,
        grossMarginGuarantee: 656.03,
      },
      {
        premium: 37.9,
        subsidy: 8.33,
        numberofHead: 1,
        marketingMonth: format(
          addMonths(baseLgmCoverageSedDate, 3),
          redshiftDateFormat,
        ),
        actualIndemnity: null,
        expectedIndemnity: null,
        actualGrossMargin: null,
        expectedGrossMargin: 576.65,
        grossMarginGuarantee: 576.65,
      },
    ],
  },
})

const lgmSwineCommodity = {
  commodity: 'Swine',
  commodityType: 'Farrow to Finish',
}

const lgmCattleCommodity = {
  commodity: 'Cattle',
  commodityType: 'Calf Finishing',
}

export const demoUserSwineLgmCoverages = [
  {
    ...baseLgmCoverage(),
    ...lgmSwineCommodity,
  },
]

export const demoUserCattleLgmCoverages = [
  {
    ...baseLgmCoverage(),
    ...lgmCattleCommodity,
  },
]

export const demoUserFedCattleCoverages = [
  {
    type: coverageTypes.lrp,
    commodity: 'Fed Cattle',
    commodityType: 'Steers & Heifers',
    status: 'Expired',
    salesEffectiveDate: '2022-11-18',
    details: {
      endDate: '2023-02-17',
      endorsementLength: '13',
      targetWeight: 10,
      numberofHead: 170,
      premium: 2.73195,
      expectedEndingValue: 155.25,
      coverageLevel: 0.99194,
      coveragePrice: 154,
      expectedIndemnity: null,
      actualEndingValue: 162,
      actualIndemnity: 0,
    },
  },
  {
    type: coverageTypes.lrp,
    commodity: 'Fed Cattle',
    commodityType: 'Steers & Heifers',
    status: 'Pending',
    salesEffectiveDate: '2023-11-07',
    details: {
      endDate: '2024-02-06',
      endorsementLength: '13',
      targetWeight: 12,
      numberofHead: 100,
      premium: 3.06605,
      expectedEndingValue: 179.081,
      coverageLevel: 0.98547,
      coveragePrice: 176.48,
      expectedIndemnity: null,
      actualEndingValue: null,
      actualIndemnity: 0,
    },
  },
]

export const demoUserFeederCattleCoverages = [
  {
    type: coverageTypes.lrp,
    commodity: 'Feeder Cattle',
    commodityType: 'Steers, <600 lbs',
    status: 'Expired',
    salesEffectiveDate: '2022-11-16',
    details: {
      endDate: '2023-02-15',
      endorsementLength: '13',
      targetWeight: 10,
      numberofHead: 200,
      expectedEndingValue: 196.853,
      coverageLevel: 0.99861,
      coveragePrice: 196.58,
      premium: 4.04624,
      expectedIndemnity: null,
      actualEndingValue: 153.599,
      actualIndemnity: 162,
    },
  },
  {
    type: coverageTypes.lrp,
    commodity: 'Feeder Cattle',
    commodityType: 'Steers, <600 lbs',
    status: 'Active',
    salesEffectiveDate: '2023-11-13',
    details: {
      endDate: '2024-03-11',
      endorsementLength: '17',
      targetWeight: 6,
      numberofHead: 210,
      expectedEndingValue: 229.945,
      coverageLevel: 0.99968,
      coveragePrice: 229.87,
      premium: 6.0554,
      expectedIndemnity: null,
      actualEndingValue: null,
      actualIndemnity: null,
    },
  },
]
